import { PERMANENT_DAYLIGHT_OFFSETS_MINUTES } from './timezoneLabels';
import { isValidIanaTimezone, FALLBACK_TIMEZONE } from './timezoneValidation';

const CANARY_INSTANT = Date.UTC(2026, 11, 1, 12);

type PolyfilledDateTimeFormat = typeof Intl.DateTimeFormat & {
  __setDefaultTimeZone: (timezone: string) => void;
};

const getOffsetMinutes = (timeZone: string, instant: number): number => {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hourCycle: 'h23',
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric'
  }).formatToParts(instant);
  const part = (type: Intl.DateTimeFormatPartTypes): number =>
    Number(parts.find((p) => p.type === type)?.value);
  const wallClock = Date.UTC(
    part('year'),
    part('month') - 1,
    part('day'),
    part('hour'),
    part('minute')
  );
  return (wallClock - instant) / 60000;
};

export const browserHasCurrentTimezoneData = (): boolean =>
  Object.entries(PERMANENT_DAYLIGHT_OFFSETS_MINUTES).every(
    ([timezone, offset]) => getOffsetMinutes(timezone, CANARY_INSTANT) === offset
  );

export const loadBundledTimezoneData = async (): Promise<void> => {
  const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  await import('@formatjs/intl-datetimeformat/polyfill-force.js');
  await Promise.all([
    import('@formatjs/intl-datetimeformat/locale-data/en.js'),
    import('@formatjs/intl-datetimeformat/add-all-tz.js')
  ]);
  // eslint-disable-next-line no-underscore-dangle -- the polyfill's only API for the default zone
  (Intl.DateTimeFormat as PolyfilledDateTimeFormat).__setDefaultTimeZone(
    isValidIanaTimezone(browserTimezone) ? browserTimezone : FALLBACK_TIMEZONE
  );
};

/**
 * Event times are converted to UTC in the browser, so a browser with tzdata older
 * than 2026e would save Canadian events an hour off. Swap in bundled tzdata there.
 */
export const ensureCurrentTimezoneData = async (): Promise<boolean> => {
  if (browserHasCurrentTimezoneData()) {
    return false;
  }
  await loadBundledTimezoneData();
  return true;
};
